'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { ArrowSquareOut, Cookie, LinkBreak } from '@phosphor-icons/react';
import { Box, StateCard } from '@unity/cloud-ui';
import { getClientEnv } from '@/lib/constants/environment';
import { useWindowValue } from '../../utils/use-window-value';

declare global {
  interface Window {
    OptanonActiveGroups?: string | string[];
  }
}

const getIsValidEmbeddedYouTubeUrl = (url: string) => {
  try {
    const { hostname, pathname } = new URL(url);

    const isYoutubeUrl = ['www.youtube.com', 'youtube.com', 'youtu.be', 'm.youtube.com'].includes(hostname);
    const isEmbedded = pathname.startsWith('/embed/');

    return isYoutubeUrl && isEmbedded;
  } catch (_) {
    return false;
  }
};

const VideoContainer = (props: React.PropsWithChildren) => {
  const { children } = props;

  return (
    <Box pb={2} width="100%">
      <Box
        height={0}
        overflow="hidden"
        paddingBottom="56.25%" // 9/16 aspect ratio
        position="relative"
        border={1}
        borderRadius="0.5rem"
        borderColor={({ vars }) => vars.palette.divider}
      >
        {children}
      </Box>
    </Box>
  );
};

const InvalidVideoEmbed = (props: React.PropsWithChildren) => {
  const { children } = props;

  return (
    <VideoContainer>
      <Box
        position="absolute"
        left={0}
        top={0}
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        {children}
      </Box>
    </VideoContainer>
  );
};

export interface VideoEmbedProps {
  src: string;
  alt: string;
}

export const VideoEmbed = (props: VideoEmbedProps) => {
  const { src, alt } = props;
  const t = useTranslations();
  const [isClient, setIsClient] = React.useState(false);

  const isOnetrustSetUp = !!getClientEnv().ONETRUST_ID;
  const isValidEmbeddedYouTubeUrl = getIsValidEmbeddedYouTubeUrl(src);

  const oneTrust = useWindowValue({
    getter: (window: Window) => window.OneTrust,
    watch: true,
  });
  const optanonActiveGroups = useWindowValue({
    getter: (window: Window) => window.OptanonActiveGroups,
    watch: true,
  });

  function handleOpenOneTrustCookieSettings() {
    oneTrust?.ToggleInfoDisplay();
  }

  const getNeedsCookies = (): boolean => {
    if (!isOnetrustSetUp) {
      return false;
    }

    if (isValidEmbeddedYouTubeUrl) {
      return optanonActiveGroups?.indexOf('C0004') === -1;
    }

    return false;
  };

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const needsCookies = getNeedsCookies();

  if (!isValidEmbeddedYouTubeUrl) {
    return (
      <InvalidVideoEmbed>
        <StateCard
          title={t('mdx:video-embed:invalid-video-link-title')}
          description={t('mdx:video-embed:invalid-video-link-description')}
          ButtonProps={{
            label: t('mdx:video-embed:learn-more'),
            // @ts-expect-error 'target' does not exist in type 'StateCardButtonProps | StateCardButtonProps[]'
            target: '_blank',
            endIcon: <ArrowSquareOut />,
            color: 'primary',
            href: 'https://support.google.com/youtube/answer/171780?hl=en',
          }}
          icon={LinkBreak}
          sx={{ width: '100%', '.MuiCardContent-root': { minHeight: 0 } }}
        />
      </InvalidVideoEmbed>
    );
  }

  if (needsCookies) {
    return (
      <InvalidVideoEmbed>
        <StateCard
          title={t('mdx:video-embed:tracking-cookies-required')}
          ButtonProps={
            oneTrust
              ? {
                  label: t('mdx:video-embed:open-cookie-settings'),
                  variant: 'text',
                  color: 'primary',
                  onClick: handleOpenOneTrustCookieSettings,
                }
              : undefined
          }
          icon={Cookie}
          sx={{ width: '100%', '.MuiCardContent-root': { minHeight: 0 } }}
        />
      </InvalidVideoEmbed>
    );
  }

  return (
    <VideoContainer>
      {/* on page load the src has enablejsapi=1 appended to it, and data-gtm-yt-inspected and id props are added to the iframe */}
      {isClient ? (
        <iframe
          allowFullScreen
          src={src}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          title={alt}
          style={{
            border: 0,
            left: 0,
            top: 0,
            height: '100%',
            width: '100%',
            position: 'absolute',
          }}
        />
      ) : null}
    </VideoContainer>
  );
};
